// Phase 2 Wave 5 Agent S1 - MFA / 2FA API client.
//
// Pairs with backend/app/api/mfa.py. All endpoints are gated server-side
// by mfa.manage_own (per-user actions) or mfa.tenant.enforce (admin).

import { api } from '@/lib/api'

export interface MFASettings {
  totp_enabled: boolean
  sms_enabled: boolean
  preferred_method: string
  recovery_codes_remaining: number
  tenant_mfa_required: boolean
}

export interface TOTPSetupResponse {
  otpauth_uri: string
  qr_data_uri: string
  secret_hint: string
}

export interface RecoveryCodesResponse {
  codes: string[]
  generated_count: number
}

export interface ChallengeResponse {
  mfa_required: true
  challenge_token: string
  method: 'totp' | 'sms'
  expires_in_seconds: number
}

export interface MFAVerifyPayload {
  challenge_token: string
  code: string
  is_recovery_code?: boolean
}

export const mfaApi = {
  settings: async (): Promise<MFASettings> => {
    const { data } = await api.get<MFASettings>('/mfa/settings')
    return data
  },

  setupTotp: async (): Promise<TOTPSetupResponse> => {
    const { data } = await api.post<TOTPSetupResponse>('/mfa/setup/totp')
    return data
  },

  verifyTotpSetup: async (code: string): Promise<MFASettings> => {
    const { data } = await api.post<MFASettings>('/mfa/setup/totp/verify', {
      code,
    })
    return data
  },

  setupSms: async (
    phone: string,
  ): Promise<{ success: boolean; live_mode: boolean }> => {
    const { data } = await api.post<{
      success: boolean
      live_mode: boolean
    }>('/mfa/setup/sms', { phone })
    return data
  },

  verifySmsSetup: async (code: string): Promise<MFASettings> => {
    const { data } = await api.post<MFASettings>('/mfa/setup/sms/verify', {
      code,
    })
    return data
  },

  recoveryCodes: async (): Promise<RecoveryCodesResponse> => {
    const { data } = await api.get<RecoveryCodesResponse>(
      '/mfa/recovery-codes',
    )
    return data
  },

  issueChallenge: async (
    email: string,
    password: string,
    method: 'totp' | 'sms' = 'totp',
  ): Promise<ChallengeResponse> => {
    const { data } = await api.post<ChallengeResponse>('/mfa/challenge', {
      email,
      password,
      method,
    })
    return data
  },

  verify: async (payload: MFAVerifyPayload): Promise<{
    access_token: string
    token_type: string
    user: any
    refresh_token?: string | null
  }> => {
    const { data } = await api.post('/mfa/verify', payload)
    return data
  },

  disable: async (password: string): Promise<{ success: boolean }> => {
    const { data } = await api.delete<{ success: boolean }>('/mfa/disable', {
      data: { password },
    })
    return data
  },

  setTenantEnforcement: async (
    required: boolean,
  ): Promise<{ tenant_id: number; mfa_required: boolean }> => {
    const { data } = await api.post<{
      tenant_id: number
      mfa_required: boolean
    }>('/mfa/tenant/enforce', { required })
    return data
  },
}
